import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { CHARACTERS } from '../lib/levels';
import { useT, COUNTRIES, defaultLangForCountry, setStoredLang, LANGS } from '../lib/i18n';

const GRADES = ['1','2','3','4','5','6','7','8','9','10','11','12','adult'];

export default function Register() {
  const { t, lang: uiLang } = useT();
  const [params] = useSearchParams();
  const initialCode = (params.get('code') || '').trim().toUpperCase();
  const [form, setForm] = useState({
    name: '', email: '', password: '',
    role: initialCode ? 'extended' : 'child', country: 'US', grade: '4',
    language: uiLang, parent_email: '', character: 'ouistiti',
    crew_invite_code: initialCode,
  });
  const [busy, setBusy] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Auto-set language when country changes (only if user hasn't manually picked)
  useEffect(() => {
    const auto = defaultLangForCountry(form.country);
    setForm((f) => ({ ...f, language: auto }));
  }, [form.country]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.role === 'child' && !form.character) {
      toast.error(t('auth.pick_character'));
      return;
    }
    if (form.role === 'extended' && !form.crew_invite_code?.trim()) {
      toast.error('Enter the invite code from the parent');
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form };
      if (payload.role !== 'child') {
        delete payload.parent_email;
        delete payload.character;
      }
      if (payload.role !== 'extended') {
        delete payload.crew_invite_code;
      } else {
        payload.crew_invite_code = payload.crew_invite_code.trim().toUpperCase();
      }
      setStoredLang(form.language);
      await register(payload);
      toast.success(`Welcome, ${form.name}!`);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Registration failed');
    } finally {
      setBusy(false);
    }
  };

  const ROLES = [
    { key: 'child',    label: t('auth.role_child'),   desc: t('auth.role_child_desc') },
    { key: 'parent',   label: t('auth.role_parent'),  desc: t('auth.role_parent_desc') },
    { key: 'teacher',  label: t('auth.role_teacher'), desc: t('auth.role_teacher_desc') },
    { key: 'extended', label: 'Extended family',      desc: 'Grandparent / uncle / aunt / friend' },
  ];

  const gradeLabel = (g) => g === 'adult' ? t('grade.adult') : `${t('grade.prefix')} ${g}`;

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-block mb-8" data-testid="register-back-home">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-2xl bg-navy flex items-center justify-center text-gold font-heading text-xl font-bold">O</div>
            <span className="font-heading text-2xl font-bold text-navy">OuiStuPaStu</span>
          </div>
        </Link>

        <div className="bg-white rounded-3xl p-6 md:p-8 shadow-squishy border-2 border-cream">
          <h1 className="font-heading text-3xl font-bold text-navy mb-2">{t('auth.register_title')}</h1>
          <p className="text-navy/60 mb-6">{t('auth.register_desc')}</p>

          <div className="grid grid-cols-2 gap-2 mb-5">
            {ROLES.map((r) => (
              <button
                key={r.key}
                type="button"
                onClick={() => upd('role', r.key)}
                className={`p-3 rounded-2xl border-2 text-left transition-colors duration-200 ${form.role === r.key ? 'border-teal bg-teal/10' : 'border-slate-200 hover:border-teal/50'}`}
                data-testid={`register-role-${r.key}`}
              >
                <div className="font-heading font-bold text-navy text-sm">{r.label}</div>
                <div className="text-[10px] text-navy/60 mt-0.5">{r.desc}</div>
              </button>
            ))}
          </div>

          {form.role === 'extended' && (
            <div className="mb-5" data-testid="crew-code-section">
              <label className="block text-sm font-semibold text-navy mb-2">Invite code from parent</label>
              <input type="text" value={form.crew_invite_code} onChange={(e) => upd('crew_invite_code', e.target.value.toUpperCase())}
                placeholder="6-char code"
                className="w-full h-14 px-4 rounded-2xl border-2 border-slate-200 focus:border-teal focus:outline-none font-mono uppercase tracking-widest"
                maxLength={6}
                data-testid="register-crew-code-input" />
              <p className="text-[11px] text-navy/60 mt-1">A parent must approve first, then share the code with you.</p>
            </div>
          )}

          {form.role === 'teacher' && (
            <div className="mb-5 bg-gradient-to-r from-gold/20 to-teal/20 border-2 border-gold rounded-2xl p-4 flex items-start gap-3" data-testid="mangahigh-banner-register">
              <div className="text-3xl shrink-0">👋</div>
              <div className="min-w-0">
                <div className="font-heading font-bold text-navy text-sm">Migrating from Mangahigh?</div>
                <div className="text-navy/70 text-xs mt-0.5">After signing up, head to the Import tab in your dashboard to upload your class list.</div>
              </div>
            </div>
          )}

          {form.role === 'child' && (
            <div className="mb-5" data-testid="character-selector">
              <label className="block text-sm font-semibold text-navy mb-2">{t('auth.pick_character')}</label>
              <div className="grid grid-cols-2 gap-3">
                {Object.values(CHARACTERS).map((c) => {
                  const selected = form.character === c.key;
                  return (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => upd('character', c.key)}
                      className={`p-3 rounded-2xl border-2 text-center transition-transform duration-200 hover:scale-[1.02] ${selected ? 'border-teal bg-teal/10 shadow-squishy' : 'border-slate-200 hover:border-teal/50 bg-white'}`}
                      data-testid={`character-card-${c.key}`}
                    >
                      <div className={`w-20 h-20 mx-auto rounded-full overflow-hidden mb-2 border-4 ${selected ? 'border-gold' : 'border-cream'}`}>
                        <img src={c.image} alt={c.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="font-heading font-bold text-navy text-sm">{c.name}</div>
                      <div className="text-[10px] text-navy/60 leading-tight mt-0.5">{c.role}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <form onSubmit={submit} className="space-y-3">
            <input type="text" required placeholder={t('auth.name')} value={form.name} onChange={(e) => upd('name', e.target.value)}
              className="w-full h-14 px-4 rounded-2xl border-2 border-slate-200 focus:border-teal focus:outline-none font-body"
              data-testid="register-name-input" />
            <input type="email" required placeholder={t('auth.email')} value={form.email} onChange={(e) => upd('email', e.target.value)}
              className="w-full h-14 px-4 rounded-2xl border-2 border-slate-200 focus:border-teal focus:outline-none font-body"
              data-testid="register-email-input" />
            <input type="password" required minLength={6} placeholder={t('auth.password')} value={form.password} onChange={(e) => upd('password', e.target.value)}
              className="w-full h-14 px-4 rounded-2xl border-2 border-slate-200 focus:border-teal focus:outline-none font-body"
              data-testid="register-password-input" />

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-navy/70 mb-1 px-1">{t('auth.country')}</label>
                <select value={form.country} onChange={(e) => upd('country', e.target.value)}
                  className="w-full h-14 px-3 rounded-2xl border-2 border-slate-200 focus:border-teal focus:outline-none font-body bg-white"
                  data-testid="register-country-select">
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>{c[`name_${uiLang}`] || c.name_en}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-navy/70 mb-1 px-1">{t('auth.grade')}</label>
                <select value={form.grade} onChange={(e) => upd('grade', e.target.value)}
                  className="w-full h-14 px-3 rounded-2xl border-2 border-slate-200 focus:border-teal focus:outline-none font-body bg-white"
                  data-testid="register-grade-select">
                  {GRADES.map((g) => <option key={g} value={g}>{gradeLabel(g)}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-navy/70 mb-1 px-1">{t('auth.language')}</label>
              <select value={form.language} onChange={(e) => upd('language', e.target.value)}
                className="w-full h-14 px-3 rounded-2xl border-2 border-slate-200 focus:border-teal focus:outline-none font-body bg-white"
                data-testid="register-language-select">
                {LANGS.map((l) => <option key={l.code} value={l.code}>{l.native}</option>)}
              </select>
            </div>

            {form.role === 'child' && (
              <input type="email" placeholder={t('auth.parent_email')} value={form.parent_email} onChange={(e) => upd('parent_email', e.target.value)}
                className="w-full h-14 px-4 rounded-2xl border-2 border-slate-200 focus:border-teal focus:outline-none font-body"
                data-testid="register-parent-email-input" />
            )}

            <button type="submit" disabled={busy}
              className="w-full h-14 rounded-full bg-gold text-navy font-heading font-bold text-lg hover:scale-[1.02] active:scale-95 transition-transform duration-200 shadow-gold disabled:opacity-60"
              data-testid="register-submit-button">
              {busy ? t('auth.creating') : t('auth.create_btn')}
            </button>
          </form>

          <p className="mt-6 text-center text-navy/70">
            {t('auth.already')}{' '}
            <Link to="/login" className="text-teal font-bold hover:underline" data-testid="register-to-login-link">
              {t('cta.log_in')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
